import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { uniqBy } from "lodash";
import { supabase } from "../../../supabaseClient";
import { getLostLeads, regenerateLead } from "../../../Services/Lead";
import { successTost, errorTost } from "../../../Services/Toast";
import Leadcard from "../../../Subcomponents/Leadcard";
import LeadStatusChip from "../../../Subcomponents/LeadStatusChip";
import { mapLeadRow } from "../mapLeadRow";

// Self-contained "Lost" tab. Lost leads come from the backend REST endpoint
// (contact-masked: no mobile/email). The endpoint filters/sorts/paginates
// server-side via the lost_leads_filtered() function and returns RPC-compatible
// fields, so we run them through the shared mapLeadRow() and render the SAME
// <Leadcard /> the other tabs use, in CardType="Lost" mode.
//
// Filters come from the shared Leads filter bar (Filter by / Sort by / Search),
// passed in via the `filters` prop, and are sent to the backend. Timeline/notes
// are NOT loaded here — the card's Timeline & Notes modal fetches them on demand.
const PAGE_SIZE = 20;
const LOST_CHIP_STYLE = { color: "#B42318", backgroundColor: "#FEF3F2" };

export default function LostLeads({ filters = {} }) {
  const loggedInUser = useSelector((state) => state.Auth.user);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [projects, setProjects] = useState([]);
  const [modalLead, setModalLead] = useState(null);
  const [selectedProject, setSelectedProject] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Track the loaded page across renders without re-triggering effects; a ref
  // guard prevents overlapping page loads from InfiniteScroll's next().
  const pageRef = useRef(1);
  const loadingRef = useRef(false);

  // Stable key so the effects re-run only when the filter values actually change.
  const filtersKey = JSON.stringify(filters || {});

  // Fetch a page. append=false replaces (initial / filter change); append=true
  // adds the next page (infinite scroll).
  const fetchPage = async (p, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (!append) setLoading(true);
    setError("");
    try {
      const res = await getLostLeads(p, PAGE_SIZE, filters);
      const mapped = (res?.data || []).map((el) => mapLeadRow(el));
      setRows((prev) => (append ? uniqBy([...prev, ...mapped], "leadId") : mapped));
      setTotal(res?.total || 0);
      pageRef.current = p;
    } catch (e) {
      if (!append) setError("Failed to load lost leads.");
    } finally {
      loadingRef.current = false;
      if (!append) setLoading(false);
    }
  };

  const loadMore = () => fetchPage(pageRef.current + 1, true);

  // (Re)load from page 1 whenever filters change (and on mount).
  useEffect(() => {
    pageRef.current = 1;
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from("Projects")
          .select("projectName")
          .eq("isVJ", true)
          .order("projectName", { ascending: true });
        setProjects((data || []).map((d) => d.projectName).filter(Boolean));
      } catch (e) {
        // non-fatal; project list just stays empty
      }
    })();
  }, []);

  const openRegen = (lead) => {
    setError("");
    setSelectedProject("");
    setModalLead(lead);
  };
  const closeRegen = () => {
    if (submitting) return;
    setModalLead(null);
    setSelectedProject("");
  };

  const submitRegen = async () => {
    if (!selectedProject || !modalLead) return;
    if (!loggedInUser?.userId) {
      errorTost("Could not determine the logged-in user.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await regenerateLead({
        leadId: modalLead.leadId,
        projectName: selectedProject,
        userId: loggedInUser.userId,
      });
      if (res?.success) {
        const name = modalLead.person?.name || modalLead.id;
        successTost(
          `Regenerated "${name}"${res.leadDisplayId ? ` → new lead VJ${res.leadDisplayId}` : ""}`
        );
        setModalLead(null);
        setSelectedProject("");
        pageRef.current = 1;
        fetchPage(1, false);
      } else {
        errorTost(res?.error || "Regeneration failed.");
      }
    } catch (e) {
      errorTost(e?.response?.data?.error || "Regeneration failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const hasActiveFilters = !!(
    filters.search ||
    filters.dateFrom ||
    filters.dateTo ||
    (filters.projects && filters.projects.length) ||
    (filters.salesIds && filters.salesIds.length) ||
    (filters.cpIds && filters.cpIds.length) ||
    (filters.leadTypes && filters.leadTypes.length) ||
    (filters.leadResponses && filters.leadResponses.length)
  );

  return (
    <div style={{ padding: "0.4rem 0 1.2rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
        <div style={{ fontFamily: "Inter-Regular", fontWeight: 600, fontSize: "1.8rem", color: "#000" }}>
          Lost Leads
        </div>
        <LeadStatusChip label={`${total} total`} style={LOST_CHIP_STYLE} />
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#991b1b", padding: "0.8rem 1rem", borderRadius: 8, marginBottom: "1rem", fontSize: "1.4rem" }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
          <CircularProgress />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ color: "#667085", fontSize: "1.4rem", padding: "2rem 0" }}>
          {hasActiveFilters ? "No lost leads match these filters." : "No lost leads."}
        </div>
      ) : (
        <InfiniteScroll
          dataLength={rows.length}
          next={loadMore}
          hasMore={rows.length < total}
          scrollableTarget="scrollableDiv"
          style={{ display: "flex", flexDirection: "column", gap: "1.6rem", overflow: "visible" }}
        >
          {rows.map((lead) => (
            <Leadcard
              key={lead.leadId}
              leadObj={lead}
              CardType="Lost"
              tabValue="Lost"
              onRegenerate={openRegen}
            />
          ))}
        </InfiniteScroll>
      )}

      {/* Regenerate modal */}
      <Dialog open={!!modalLead} onClose={closeRegen} maxWidth="xs" fullWidth>
        <DialogTitle>Regenerate lead</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: "1.4rem", color: "#475569", marginBottom: "1.2rem" }}>
            A fresh lead will be created for <b>{modalLead?.person?.name || modalLead?.id}</b> and assigned to you. Select the project to regenerate under:
          </div>
          <FormControl fullWidth size="small">
            <InputLabel id="regen-project-label">Project</InputLabel>
            <Select
              labelId="regen-project-label"
              label="Project"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              {projects.map((p) => (
                <MenuItem key={p} value={p} style={{ fontSize: "1.4rem" }}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRegen} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={submitRegen} disabled={!selectedProject || submitting}>
            {submitting ? "Regenerating…" : "Regenerate"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
